import PizZip from 'pizzip';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export type ProjectOption = {
  id: number;
  title: string;
  nom_client?: string | null;
  idclient?: number | null;
  status?: string | null;
};

export type TaskOption = {
  id: number;
  titre: string;
  status_text?: string | null;
  isjalon?: boolean | null;
};

export type ImportedPdfSource = {
  fileName: string;
  bytes: number;
  pageCount: number;
  firstPageImageBytes?: Uint8Array;
  raw: string;
  parsed: SaintPdfSummary;
};

export type ImportedTextSource = {
  fileName: string;
  bytes: number;
  lineCount: number;
  uniqueIpCount: number;
  sampleIps: string[];
  raw: string;
};

export type VulnerabilityRow = {
  severity: string;
  score: number;
  ip: string;
  port: string;
  category: string;
  name: string;
  affectedAsset: string;
  remediation: string;
};

export type TargetAssetRow = {
  functionLabel: string;
  name: string;
  address: string;
  probe: string;
};

export type ScanProbe = {
  id: string;
  label: string;
  probeType: 'client_internal' | 'bz_external' | 'bz_cloud' | 'custom';
  locationLabel: string;
  scopeLabel: string;
  reportDescription: string;
};

export type TargetProbeAssignment = {
  targetKey: string;
  target: TargetAssetRow;
  suggestedProbeId: string;
  validatedProbeId: string;
  confidence: 'Forte' | 'Moyenne' | 'Faible';
  status: 'Validé' | 'À valider' | 'Corrigé par conseiller' | 'Non assigné';
};

export type ProbeFilter = 'all' | 'needs_validation' | 'unassigned' | 'corrected';

export const DEFAULT_SCAN_PROBES: ScanProbe[] = [
  {
    id: 'probe_client_internal',
    label: 'Sonde interne client',
    probeType: 'client_internal',
    locationLabel: 'Bureau du client',
    scopeLabel: 'Réseau interne',
    reportDescription: 'Sonde installée dans les locaux du client pour analyser les actifs accessibles depuis le réseau interne.',
  },
  {
    id: 'probe_bz_external',
    label: 'Sonde externe BZ',
    probeType: 'bz_external',
    locationLabel: 'Internet / BZ',
    scopeLabel: 'Adresses IP publiques',
    reportDescription: 'Sonde externe BZ utilisée pour analyser les adresses IP publiques et services exposés sur Internet.',
  },
  {
    id: 'probe_bz_cloud',
    label: 'Sonde infonuagique BZ',
    probeType: 'bz_cloud',
    locationLabel: 'Environnement infonuagique BZ',
    scopeLabel: 'Segment infonuagique',
    reportDescription: 'Sonde située dans l’environnement infonuagique de BZ pour analyser les actifs accessibles depuis ce segment hébergé.',
  },
];

export type SaintPdfSummary = {
  totalVulnerabilities: number;
  lowCount: number;
  mediumCount: number;
  highCount: number;
  criticalCount: number;
  scannedHosts: number;
  maxScore: number;
  monthYear: string | null;
  reportDate: string | null;
  targets: TargetAssetRow[];
  recommendations: VulnerabilityRow[];
};

export type GeneratedDraft = {
  title: string;
  body: string;
  generatedAt: string;
  docxFileName?: string;
  docxBlob?: Blob;
};

export const TEMPLATE_URL = '/templates/gabarit-rapport-balayage-vulnerabilites-v2025.docx';

export function extractIps(content: string) {
  return Array.from(new Set(content.match(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g) || [])).filter((ip) =>
    ip.split('.').every((part) => Number(part) >= 0 && Number(part) <= 255)
  );
}

export function isPrivateIp(ip: string) {
  const parts = ip.split('.').map(Number);
  if (parts[0] === 10) return true;
  if (parts[0] === 192 && parts[1] === 168) return true;
  if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true;
  return false;
}

export function isLikelyCloudPrivateIp(ip: string) {
  return /^10\./.test(ip) || /^172\.(1[6-9]|2\d|3[01])\./.test(ip);
}

export function suggestProbeForTarget(target: TargetAssetRow, fortigateIps = new Set<string>()): Pick<TargetProbeAssignment, 'suggestedProbeId' | 'validatedProbeId' | 'confidence' | 'status'> {
  const value = target.address || '';
  const firstIp = (value.match(/\b(?:\d{1,3}\.){3}\d{1,3}\b/) || [])[0] || value;
  if (!isPrivateIp(firstIp)) {
    return { suggestedProbeId: 'probe_bz_external', validatedProbeId: 'probe_bz_external', confidence: 'Forte', status: 'Validé' };
  }
  if (fortigateIps.has(firstIp)) {
    return { suggestedProbeId: 'probe_client_internal', validatedProbeId: 'probe_client_internal', confidence: 'Forte', status: 'Validé' };
  }
  if (isLikelyCloudPrivateIp(firstIp)) {
    return { suggestedProbeId: 'probe_bz_cloud', validatedProbeId: 'probe_bz_cloud', confidence: 'Moyenne', status: 'À valider' };
  }
  return { suggestedProbeId: 'probe_client_internal', validatedProbeId: 'probe_client_internal', confidence: 'Moyenne', status: 'À valider' };
}

export function buildProbeAssignments(targets: TargetAssetRow[], fortigateRaw = ''): TargetProbeAssignment[] {
  const fortigateIps = new Set(extractIps(fortigateRaw));
  return targets.map((target, index) => {
    const suggestion = suggestProbeForTarget(target, fortigateIps);
    return {
      targetKey: `${target.address}-${target.name}-${index}`,
      target,
      ...suggestion,
    };
  });
}

export function getProbeLabel(probes: ScanProbe[], probeId: string) {
  return probes.find((probe) => probe.id === probeId)?.label || 'Sonde à préciser';
}

export function applyValidatedProbeLabels(assignments: TargetProbeAssignment[], probes: ScanProbe[]) {
  return assignments.map((assignment) => ({
    ...assignment.target,
    probe: getProbeLabel(probes, assignment.validatedProbeId),
  }));
}

export function uniqueProbeLabels(targets: TargetAssetRow[]) {
  return Array.from(new Set(targets.map((target) => target.probe).filter(Boolean)));
}

export function probeSentence(label: string) {
  const token = label.toLowerCase();
  if (token.includes('interne')) return `${label} installée sur le réseau du client.`;
  if (token.includes('externe')) return `${label} située chez BZ.`;
  if (token.includes('infonuagique')) return `${label} située dans l’environnement infonuagique de BZ.`;
  return `${label} utilisée pour une portion spécifique du balayage.`;
}

export function buildProbeNarrative(targets: TargetAssetRow[]) {
  const labels = uniqueProbeLabels(targets);
  const multiple = labels.length > 1;
  return {
    intro: multiple
      ? 'Selon les cibles analysées, le balayage a été réalisé à l’aide des sondes suivantes :'
      : 'Le balayage a été réalisé à l’aide de la sonde suivante :',
    firstProbe: labels[0] ? probeSentence(labels[0]) : '',
    otherProbes: labels.slice(1).map(probeSentence).join(' '),
    setupParagraph: multiple
      ? 'Des dispositifs ont été déployés afin de couvrir les différentes portions du balayage, notamment les analyses internes et externes selon les cibles validées.'
      : 'Un dispositif a été déployé afin de couvrir la portion du balayage associée aux cibles validées.',
    deviceLead: multiple ? 'Les dispositifs ont été configurés' : 'Le dispositif a été configuré',
  };
}

export function sourceSummary(raw: string, fileName: string): ImportedTextSource {
  const ips = extractIps(raw);
  return {
    fileName,
    bytes: new Blob([raw]).size,
    lineCount: raw.split(/\r?\n/).filter(Boolean).length,
    uniqueIpCount: ips.length,
    sampleIps: ips.slice(0, 8),
    raw,
  };
}

export function escapeXml(value: string | number) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function slugify(value: string) {
  return value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'client';
}

export function sanitizeFileName(value: string) {
  return value.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function roundedDownPageCount(pageCount: number) {
  if (pageCount < 10) return pageCount;
  return Math.floor(pageCount / 10) * 10;
}

export function buildScanReportDocxFileName(clientName: string, monthYear: string) {
  return `${sanitizeFileName(clientName)} - Rapport de balayage de vulnérabilités - ${sanitizeFileName(monthYear)}.docx`;
}

export function downloadBlob(fileName: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function downloadTextFile(fileName: string, content: string) {
  downloadBlob(fileName, new Blob([content], { type: 'text/markdown;charset=utf-8' }));
}

export function replaceTemplateText(xml: string, replacements: Record<string, string | number>) {
  let next = xml;
  for (const [key, value] of Object.entries(replacements)) {
    next = next.split(key).join(escapeXml(value));
  }
  return next;
}

export function replaceInlineTextInParagraph(paragraphXml: string, searchText: string, value: string | number) {
  const textMatches = [...paragraphXml.matchAll(/<w:t(?=[\s>])([^>]*)>([\s\S]*?)<\/w:t>/g)];
  const plainText = textMatches.map((match) => match[2]).join('');
  const start = plainText.indexOf(searchText);
  if (start === -1) return paragraphXml;
  const end = start + searchText.length;
  let cursor = 0;
  let inserted = false;
  let next = paragraphXml;
  for (const match of textMatches) {
    const fullMatch = match[0];
    const attrs = match[1];
    const text = match[2];
    const textStart = cursor;
    const textEnd = cursor + text.length;
    cursor = textEnd;
    if (textEnd <= start || textStart >= end) continue;
    const before = textStart < start ? text.slice(0, start - textStart) : '';
    const after = textEnd > end ? text.slice(end - textStart) : '';
    const replacementText = inserted ? after : `${before}${escapeXml(value)}${after}`;
    inserted = true;
    const xmlSpace = attrs.includes('xml:space=') ? attrs : `${attrs} xml:space="preserve"`;
    next = next.replace(fullMatch, `<w:t${xmlSpace}>${replacementText}</w:t>`);
  }
  return next;
}

export function replaceInlineTextAcrossRuns(xml: string, replacements: Record<string, string | number>) {
  let next = xml;
  for (const [searchText, value] of Object.entries(replacements)) {
    next = next.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/g, (paragraph) => replaceInlineTextInParagraph(paragraph, searchText, value));
  }
  return next;
}

type PizZipTextFile = {
  name: string;
  asText: () => string;
};

export function replaceTemplateTextInDocx(zip: PizZip, replacements: Record<string, string | number>) {
  zip.file(/word\/(document|header\d+|footer\d+)\.xml/).forEach((file: PizZipTextFile) => {
    zip.file(file.name, replaceTemplateText(file.asText(), replacements));
  });
}

export function replaceXmlElementText(xml: string, elementName: string, value: string | number) {
  const escapedValue = escapeXml(value);
  const escapedElement = elementName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`(<${escapedElement}(?:\\s[^>]*)?>)[\\s\\S]*?(<\\/${escapedElement}>)`);
  return xml.replace(pattern, `$1${escapedValue}$2`);
}

export function updateScanReportDocumentProperties(zip: PizZip, clientName: string, monthYear: string) {
  const coreFile = zip.file('docProps/core.xml');
  if (coreFile) {
    let coreXml = coreFile.asText();
    coreXml = replaceXmlElementText(coreXml, 'dc:title', clientName);
    coreXml = replaceXmlElementText(coreXml, 'dc:subject', monthYear);
    zip.file('docProps/core.xml', coreXml);
  }
  const appFile = zip.file('docProps/app.xml');
  if (appFile) {
    const appXml = appFile.asText()
      .replace(/<vt:lpstr>\[Nom du client\]<\/vt:lpstr>/g, `<vt:lpstr>${escapeXml(clientName)}</vt:lpstr>`)
      .replace(/<vt:lpstr>Mois Année<\/vt:lpstr>/g, `<vt:lpstr>${escapeXml(monthYear)}</vt:lpstr>`);
    zip.file('docProps/app.xml', appXml);
  }
}

export function removeTextHighlighting(xml: string) {
  return xml.replace(/<w:highlight[^>]*\/>/g, '');
}

export function finalizeGeneratedDocxXml(zip: PizZip) {
  const finalReplacements = {
    'Annexe A': 'annexe A',
  };
  zip.file(/word\/(document|header\d+|footer\d+)\.xml/).forEach((file: PizZipTextFile) => {
    zip.file(file.name, removeTextHighlighting(replaceInlineTextAcrossRuns(replaceTemplateText(file.asText(), finalReplacements), finalReplacements)));
  });
}

export function replaceFirstStandaloneText(xml: string, searchText: string, value: string | number, startIndex = 0) {
  const escapedSearch = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`<w:t(?=[\\s>])([^>]*)>${escapedSearch}<\\/w:t>`, 'g');
  pattern.lastIndex = startIndex;
  const match = pattern.exec(xml);
  if (!match) return xml;
  const replacement = `<w:t${match[1]}>${escapeXml(value)}</w:t>`;
  return `${xml.slice(0, match.index)}${replacement}${xml.slice(match.index + match[0].length)}`;
}

export function replaceParagraphText(paragraphXml: string, value: string | number) {
  let replaced = false;
  return paragraphXml.replace(/<w:t(?=[\s>])([^>]*)>[\s\S]*?<\/w:t>/g, (_match, attrs) => {
    if (replaced) return `<w:t${attrs}></w:t>`;
    replaced = true;
    const xmlSpace = attrs.includes('xml:space=') ? attrs : `${attrs} xml:space="preserve"`;
    return `<w:t${xmlSpace}>${escapeXml(value)}</w:t>`;
  });
}

export function getParagraphPlainText(paragraphXml: string) {
  return paragraphXml
    .replace(/<w:t(?=[\s>])[^>]*>([\s\S]*?)<\/w:t>/g, '$1')
    .replace(/<[^>]+>/g, '');
}

export function replaceParagraphContaining(xml: string, containsText: string, value: string | number) {
  return xml.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/g, (paragraph) => {
    const plainText = getParagraphPlainText(paragraph);
    return plainText.includes(containsText) ? replaceParagraphText(paragraph, value) : paragraph;
  });
}

export function replaceAnalysisParagraphs(xml: string, summary: SaintPdfSummary, pageCount: number) {
  const total = summary.totalVulnerabilities || summary.recommendations.length;
  const maxScore = summary.maxScore || Math.max(...summary.recommendations.map((row) => row.score), 0);
  const critical = summary.criticalCount || summary.recommendations.filter((row) => row.severity === 'Critical').length;
  const high = summary.highCount || summary.recommendations.filter((row) => row.severity === 'High').length;
  const roundedPages = roundedDownPageCount(pageCount);
  let next = replaceTemplateText(xml, {
    'X vulnérabilités': `${total} vulnérabilités`,
    'de X vulnérabilités': `de ${total} vulnérabilités`,
    'de X \nvulnérabilités': `de ${total} vulnérabilités`,
    'un score CVSS de X': `un score CVSS de ${maxScore.toFixed(1)}`,
    'X critiques': `${critical} critiques`,
    'X élevées': `${high} élevées`,
    'les classant dans la catégorie critique': `dont ${critical} critiques et ${high} élevées, les classant dans la catégorie critique`,
    'CVSS de X ou plus': 'CVSS de 7.0 ou plus',
    'plus de 40 pages': `plus de ${roundedPages} pages`,
  });
  next = replaceParagraphContaining(next, 'présentent un total de', `Les résultats détaillés en annexe révèlent que les cibles analysées présentent un total de ${total} vulnérabilités. Certaines atteignent un score CVSS de ${maxScore.toFixed(1)}, dont ${critical} critiques et ${high} élevées. Ce niveau de gravité indique qu’elles sont facilement exploitables et susceptibles d’engendrer des conséquences majeures sur les systèmes affectés.`);
  next = replaceParagraphContaining(next, 'on dénombre notamment', `Parmi les vulnérabilités détectées, on dénombre notamment ${critical} critiques et ${high} élevées. Ces constats démontrent qu’un certain nombre de mesures correctives devraient être mises en œuvre afin de renforcer la posture de sécurité des actifs TI concernés.`);
  next = replaceParagraphContaining(next, 'Il a révélé un total', `Le balayage de vulnérabilités effectué par BZ offre un portrait clair de l’état de sécurité des actifs TI analysés. Il a révélé un total de ${total} vulnérabilités, dont ${critical} critiques et ${high} élevées selon l’échelle CVSS. Elles présentent un risque significatif en raison de leur exploitabilité et du potentiel d’impact sur la confidentialité, l’intégrité et la disponibilité des systèmes.`);
  return next;
}

export function splitTableRows(tableXml: string) {
  return [...tableXml.matchAll(/<w:tr(?=[\s>])[\s\S]*?<\/w:tr>/g)].map((match) => match[0]);
}

export function splitRowCells(rowXml: string) {
  return [...rowXml.matchAll(/<w:tc(?=[\s>])[\s\S]*?<\/w:tc>/g)].map((match) => match[0]);
}

export function cloneStyledTableRow(rowXml: string) {
  return rowXml
    .replace(/w14:paraId="[^"]+"/g, '')
    .replace(/w14:textId="[^"]+"/g, '')
    .replace(/w:rsid(?:R|RPr|RDefault|P|Tr)="[^"]+"/g, '');
}

export function setCellVerticalMerge(cellXml: string, mode?: 'restart' | 'continue') {
  let next = cellXml.replace(/<w:vMerge(?:\s+[^>]*)?\/>/g, '');
  if (!mode) return next;
  const vMergeXml = mode === 'restart' ? '<w:vMerge w:val="restart"/>' : '<w:vMerge/>';
  return next.replace(/(<w:tcPr[^>]*>)/, `$1${vMergeXml}`);
}

export function ensureParagraphCentered(paragraphXml: string) {
  if (/<w:pPr(?=[\s>])/.test(paragraphXml)) {
    return paragraphXml.replace(/<w:jc\s+[^>]*\/>/g, '').replace(/(<w:pPr(?=[\s>])[^>]*>)/, '$1<w:jc w:val="center"/>');
  }
  return paragraphXml.replace(/(<w:p(?=[\s>])[^>]*>)/, '$1<w:pPr><w:jc w:val="center"/></w:pPr>');
}

export function ensureTableCellFormatting(cellXml: string) {
  let next = cellXml.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/g, (paragraph) => ensureParagraphCentered(paragraph));
  if (/<w:vAlign\s+[^>]*\/>/.test(next)) {
    next = next.replace(/<w:vAlign\s+[^>]*\/>/g, '<w:vAlign w:val="center"/>');
  } else if (/<w:tcPr(?=[\s>])/.test(next)) {
    next = next.replace(/(<w:tcPr(?=[\s>])[^>]*>)/, '$1<w:vAlign w:val="center"/>');
  }
  return next;
}

export function trimTableCellText(cellXml: string) {
  return cellXml.replace(/<w:t(?=[\s>])([^>]*)>([\s\S]*?)<\/w:t>/g, (_match, attrs, text) => `<w:t${attrs}>${String(text ?? '').trim()}</w:t>`);
}

export function ensureTableCentered(tableXml: string) {
  if (/<w:tblPr(?=[\s>])/.test(tableXml)) {
    return tableXml
      .replace(/<w:jc\s+[^>]*\/>/g, '')
      .replace(/(<w:tblPr(?=[\s>])[^>]*>)/, '$1<w:jc w:val="center"/>');
  }
  return tableXml.replace(/(<w:tbl(?=[\s>])[^>]*>)/, '$1<w:tblPr><w:jc w:val="center"/></w:tblPr>');
}

export function setCellWidth(cellXml: string, width?: number) {
  if (!width) return cellXml;
  const widthXml = `<w:tcW w:w="${width}" w:type="dxa"/>`;
  if (/<w:tcW\s+[^>]*\/>/.test(cellXml)) return cellXml.replace(/<w:tcW\s+[^>]*\/>/, widthXml);
  return cellXml.replace(/(<w:tcPr(?=[\s>])[^>]*>)/, `$1${widthXml}`);
}

export function replaceFirstTextInCell(cellXml: string, value: string | number) {
  const replacement = escapeXml(String(value ?? '').trim());
  let replaced = false;
  const next = cellXml.replace(/<w:t(?=[\s>])([^>]*)>[\s\S]*?<\/w:t>/g, (match, attrs) => {
    if (replaced) return `<w:t${attrs}></w:t>`;
    replaced = true;
    const xmlSpace = attrs.includes('xml:space=') ? attrs : `${attrs} xml:space="preserve"`;
    return `<w:t${xmlSpace}>${replacement}</w:t>`;
  });
  if (replaced) return next;
  return next.replace('</w:tc>', `<w:p><w:r><w:t xml:space="preserve">${replacement}</w:t></w:r></w:p></w:tc>`);
}

export function formatRowCells(rowXml: string, columnWidths: number[] = []) {
  const cells = splitRowCells(rowXml);
  let next = rowXml;
  cells.forEach((cell, index) => {
    next = next.replace(cell, ensureTableCellFormatting(setCellWidth(cell, columnWidths[index])));
  });
  return next;
}

export function replaceRowCellTexts(rowXml: string, values: Array<string | number>, mergeRestartColumns: number[] = [], mergeContinueColumns: number[] = [], columnWidths: number[] = []) {
  const cells = splitRowCells(rowXml);
  if (!cells.length) return rowXml;
  let next = rowXml;
  cells.forEach((cell, index) => {
    const mergeMode = mergeRestartColumns.includes(index) ? 'restart' : mergeContinueColumns.includes(index) ? 'continue' : undefined;
    const styledCell = setCellWidth(setCellVerticalMerge(cell, mergeMode), columnWidths[index]);
    const filledCell = ensureTableCellFormatting(replaceFirstTextInCell(styledCell, values[index] ?? ''));
    next = next.replace(cell, filledCell);
  });
  return next;
}

export function sectionRowXml(templateRow: string, label: string) {
  return replaceRowCellTexts(cloneStyledTableRow(templateRow), [label.toUpperCase()]);
}

export function targetSectionFor(functionLabel: string) {
  const token = functionLabel.toLowerCase();
  if (/pare-feu|commutateur|réseau|reseau|wi-?fi|point d’accès|unifi|forti/.test(token)) return 'Équipements réseaux';
  if (/imprimante|traceur|périphérique|peripherique|téléphone|telephone/.test(token)) return 'Imprimantes et périphériques';
  if (/serveur|domaine|sql|base de données|web|terminaux|hyper-v|applicatif/.test(token)) return 'Serveurs';
  return 'Autres actifs analysés';
}

export function buildTargetTableRowsFromTemplate(tableXml: string, targets: TargetAssetRow[]) {
  const rows = splitTableRows(tableXml);
  if (rows.length < 2) return rows;
  const headerRow = rows[0];
  const sectionTemplate = rows.find((row) => /<w:gridSpan/.test(row)) || rows[1];
  const dataTemplate = rows.find((row, index) => index > 0 && splitRowCells(row).length >= 3 && !/<w:gridSpan/.test(row)) || rows[rows.length - 1];
  const columnWidths = [2200, 2600, 2400, 2200];
  const grouped = new Map<string, TargetAssetRow[]>();
  for (const target of targets.slice(0, 60)) {
    const section = targetSectionFor(target.functionLabel);
    grouped.set(section, [...(grouped.get(section) || []), target]);
  }
  const generated = [formatRowCells(headerRow, columnWidths)];
  for (const [section, sectionTargets] of grouped.entries()) {
    generated.push(sectionRowXml(sectionTemplate, section));
    let previousFunction = '';
    sectionTargets.forEach((target) => {
      const repeatedFunction = previousFunction === target.functionLabel;
      generated.push(replaceRowCellTexts(
        cloneStyledTableRow(dataTemplate),
        [repeatedFunction ? '' : target.functionLabel, target.name, target.address, target.probe],
        repeatedFunction ? [] : [0],
        repeatedFunction ? [0] : [],
        columnWidths
      ));
      previousFunction = target.functionLabel;
    });
  }
  return generated;
}

export function buildRecommendationTableRowsFromTemplate(tableXml: string, recommendations: VulnerabilityRow[]) {
  const rows = splitTableRows(tableXml);
  if (rows.length < 2) return rows;
  const headerRow = rows[0];
  const dataTemplate = rows.find((row, index) => index > 0 && splitRowCells(row).length >= 4) || rows[rows.length - 1];
  const columnWidths = [1200, 2800, 2200, 3300];
  const generated = [formatRowCells(headerRow, columnWidths)];
  let previousKey = '';
  for (const row of recommendations.slice(0, 36)) {
    const vulnerability = translateVulnerability(row.name);
    const key = `${row.score.toFixed(1)}|${vulnerability}|${row.remediation}`;
    const repeated = key === previousKey;
    generated.push(replaceRowCellTexts(
      cloneStyledTableRow(dataTemplate),
      [repeated ? '' : row.score.toFixed(1), repeated ? '' : vulnerability, row.affectedAsset, repeated ? '' : row.remediation],
      repeated ? [] : [0, 1, 3],
      repeated ? [0, 1, 3] : [],
      columnWidths
    ));
    previousKey = key;
  }
  return generated;
}

export function replaceTableRowsByIndex(xml: string, tableIndex: number, buildRows: (tableXml: string) => string[]) {
  const tables = [...xml.matchAll(/<w:tbl(?=[\s>])[\s\S]*?<\/w:tbl>/g)];
  const match = tables[tableIndex];
  if (!match) return xml;
  const tableXml = match[0];
  const rowMatches = [...tableXml.matchAll(/<w:tr(?=[\s>])[\s\S]*?<\/w:tr>/g)];
  if (!rowMatches.length) return xml;
  const firstRow = rowMatches[0];
  const lastRow = rowMatches[rowMatches.length - 1];
  const prefix = tableXml.slice(0, firstRow.index || 0);
  const suffix = tableXml.slice((lastRow.index || 0) + lastRow[0].length);
  const generatedRows = buildRows(tableXml).join('');
  const nextTable = ensureTableCentered(`${prefix}${generatedRows}${suffix}`);
  return `${xml.slice(0, match.index || 0)}${nextTable}${xml.slice((match.index || 0) + tableXml.length)}`;
}

export function fillTargetTableRows(xml: string, targets: TargetAssetRow[]) {
  return replaceTableRowsByIndex(xml, 1, (tableXml) => buildTargetTableRowsFromTemplate(tableXml, targets));
}

export function fillRecommendationTableRows(xml: string, recommendations: VulnerabilityRow[]) {
  return replaceTableRowsByIndex(xml, 2, (tableXml) => buildRecommendationTableRowsFromTemplate(tableXml, recommendations));
}

export function replaceScanReportPreviewImage(zip: PizZip, firstPageImageBytes?: Uint8Array) {
  if (!firstPageImageBytes?.length) return;
  zip.file('word/media/image5.png', firstPageImageBytes, { binary: true });
}

export function base64ToUint8Array(value: string) {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

export function normalizeLines(text: string) {
  return text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
}

export function parseNumberAfter(label: RegExp, text: string) {
  const match = text.match(label);
  return match ? Number(match[1]) || 0 : 0;
}

const frenchMonthMap: Record<string, string> = {
  jan: 'janvier', january: 'janvier',
  feb: 'février', february: 'février',
  mar: 'mars', march: 'mars',
  apr: 'avril', april: 'avril',
  may: 'mai',
  jun: 'juin', june: 'juin',
  jul: 'juillet', july: 'juillet',
  aug: 'août', august: 'août',
  sep: 'septembre', sept: 'septembre', september: 'septembre',
  oct: 'octobre', october: 'octobre',
  nov: 'novembre', november: 'novembre',
  dec: 'décembre', december: 'décembre',
};

export function formatFrenchMonthYear(month: string, year: string) {
  return `${frenchMonthMap[month.toLowerCase()] || month.toLowerCase()} ${year}`;
}

export function detectScanCompletedMonthYear(text: string) {
  const scanCompletedMatch = text.match(/Scan completed:\s*([A-Za-z]{3,9})\s+\d{1,2},?\s*(20\d{2})/i);
  if (!scanCompletedMatch) return null;
  return formatFrenchMonthYear(scanCompletedMatch[1], scanCompletedMatch[2]);
}

export function detectMonthYear(text: string) {
  const titleMatch = text.match(/-\s*(January|February|March|April|May|June|July|August|September|October|November|December)\s+(20\d{2})/i);
  if (!titleMatch) return null;
  return formatFrenchMonthYear(titleMatch[1], titleMatch[2]);
}

export function detectReportDate(text: string) {
  const match = text.match(/Report Generated:\s*([A-Za-z]+)\s+(\d{1,2}),\s*(20\d{2})/i);
  if (!match) return null;
  const monthMap = frenchMonthMap;
  return `${Number(match[2])} ${monthMap[match[1].toLowerCase()]} ${match[3]}`;
}

export function inferAssetFunction(name: string, ip: string) {
  const token = `${name} ${ip}`.toLowerCase();
  if (/dc|domain|ldap|kerberos/.test(token)) return 'Contrôleur de domaine';
  if (/sql|bd|database|visionbd/.test(token)) return 'Serveur de base de données';
  if (/print|printer|hp|npi|designjet|imprim/.test(token)) return 'Imprimante';
  if (/api|app|mfiles|server|srv|windows|linux/.test(token)) return 'Serveur';
  if (/firewall|forti|pare-feu/.test(token)) return 'Pare-feu périmétrique';
  return 'Actif TI analysé';
}

export function parseHostTargets(lines: string[]) {
  const targets: TargetAssetRow[] = [];
  const hostStart = lines.findIndex((line) => line === '2.1 Host List');
  const hostEnd = lines.findIndex((line, index) => index > hostStart && line === '2.2 Vulnerability List');
  const section = lines.slice(hostStart > -1 ? hostStart : 0, hostEnd > -1 ? hostEnd : Math.min(lines.length, 250));
  const seen = new Set<string>();
  for (let index = 0; index < section.length; index += 1) {
    const ip = section[index];
    if (!/^\d{1,3}(?:\.\d{1,3}){3}$/.test(ip) || seen.has(ip)) continue;
    const previous = section[index - 1] || '';
    const name = /^[A-Za-z0-9_.-]{3,}$/.test(previous) && !/Score|Address|CVSS|Name/i.test(previous) ? previous : '';
    seen.add(ip);
    targets.push({ functionLabel: inferAssetFunction(name, ip), name: name || 'À valider', address: ip, probe: 'Interne' });
  }
  return targets;
}

export function translateVulnerability(name: string) {
  return name
    .replace(/vulnerable Apache version/i, 'Version vulnérable d’Apache')
    .replace(/vulnerable Lighttpd version/i, 'Version vulnérable de Lighttpd')
    .replace(/vulnerable nginx HTTP server version/i, 'Version vulnérable de nginx')
    .replace(/Microsoft SQL Server vulnerable version/i, 'Version vulnérable de Microsoft SQL Server')
    .replace(/Web server allows/i, 'Le serveur Web permet')
    .replace(/SSL\/TLS server supports/i, 'Le serveur SSL/TLS prend en charge')
    .replace(/guessable read community string/i, 'Chaîne de communauté SNMP en lecture devinable')
    .replace(/guessable write community string/i, 'Chaîne de communauté SNMP en écriture devinable')
    .replace(/default device password/i, 'Mot de passe par défaut de l’équipement')
    .replace(/possible vulnerability/i, 'Vulnérabilité potentielle')
    .replace(/readable share/i, 'Partage lisible');
}

export function remediationFor(name: string, category: string) {
  const token = `${name} ${category}`.toLowerCase();
  if (/apache|lighttpd|nginx|jquery|glpi|version/.test(token)) return 'Mettre à niveau le logiciel vers une version corrigée et supportée, puis confirmer la correction par un nouveau balayage.';
  if (/ssl|tls|certificate|cipher|sweet32|rc4/.test(token)) return 'Remplacer ou reconfigurer les certificats et suites cryptographiques afin d’éliminer les protocoles, chiffrements ou paramètres faibles.';
  if (/snmp|community/.test(token)) return 'Désactiver SNMP lorsque non requis ou remplacer les communautés par des valeurs robustes et restreindre l’accès réseau.';
  if (/password|ftp|telnet|cleartext/.test(token)) return 'Désactiver les accès non sécurisés, imposer des mots de passe robustes et privilégier des protocoles chiffrés.';
  if (/http method|put|delete/.test(token)) return 'Désactiver les méthodes HTTP non nécessaires et limiter les droits d’écriture sur le serveur Web.';
  if (/share|smb|null session/.test(token)) return 'Restreindre les partages et sessions anonymes, puis valider les permissions SMB.';
  return 'Appliquer le correctif recommandé par l’éditeur ou ajuster la configuration du service affecté, puis valider par un nouveau balayage.';
}

export function parseVulnerabilityRows(lines: string[]) {
  const rows: VulnerabilityRow[] = [];
  const start = lines.findIndex((line) => line === '2.2 Vulnerability List');
  const section = lines.slice(start > -1 ? start : 0, Math.min(lines.length, start > -1 ? start + 900 : 900));
  for (let index = 0; index < section.length; index += 1) {
    const severity = section[index];
    if (!['Critical', 'High'].includes(severity)) continue;
    const score = Number(section[index + 1]);
    if (!Number.isFinite(score)) continue;
    const ipOffset = section.slice(index + 2, index + 8).findIndex((value) => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(value));
    if (ipOffset < 0) continue;
    const ipIndex = index + 2 + ipOffset;
    const ip = section[ipIndex];
    const portParts: string[] = [];
    let cursor = ipIndex + 1;
    while (cursor < section.length && !/^(Web|Other|Databases|Networking|SNMP|Windows|OS|File Transfer|Print|Services|Login|Shell)$/i.test(section[cursor]) && !['Critical', 'High', 'Medium', 'Low'].includes(section[cursor])) {
      portParts.push(section[cursor]);
      cursor += 1;
      if (portParts.join('').includes('/')) break;
    }
    const categoryParts: string[] = [];
    while (cursor < section.length && !['Critical', 'High', 'Medium', 'Low'].includes(section[cursor]) && categoryParts.length < 3) {
      if (/^[a-z]/.test(section[cursor]) || section[cursor].length > 26) break;
      categoryParts.push(section[cursor]);
      cursor += 1;
    }
    const nameParts: string[] = [];
    while (cursor < section.length && !['Critical', 'High', 'Medium', 'Low'].includes(section[cursor]) && nameParts.join(' ').length < 160) {
      nameParts.push(section[cursor]);
      cursor += 1;
    }
    const name = nameParts.join(' ').replace(/\s+/g, ' ').trim();
    if (!name) continue;
    const category = categoryParts.join(' / ') || 'Autre';
    rows.push({ severity, score, ip, port: portParts.join(''), category, name, affectedAsset: ip, remediation: remediationFor(name, category) });
  }
  const unique = new Map<string, VulnerabilityRow>();
  for (const row of rows.sort((a, b) => b.score - a.score)) {
    const key = `${row.score}-${row.ip}-${row.name}`;
    if (!unique.has(key)) unique.set(key, row);
  }
  return Array.from(unique.values());
}

export function parseSaintPdfText(text: string, fileName = 'rapport-complet.pdf'): SaintPdfSummary {
  const lines = normalizeLines(text);
  const total = parseNumberAfter(/Vulnerabilities\s+(\d+)/i, text);
  const low = parseNumberAfter(/Low Severity\s+(\d+)/i, text);
  const medium = parseNumberAfter(/Medium Severity\s+(\d+)/i, text);
  const high = parseNumberAfter(/High Severity\s+(\d+)/i, text);
  const critical = parseNumberAfter(/Critical Vulnerabilities\s+(\d+)/i, text);
  const hosts = parseNumberAfter(/Scanned\s+Hosts\s+(\d+)/i, text);
  const maxScore = Number((text.match(/Highest CVSSv3 Base Score\s+(\d+(?:\.\d+)?)/i) || [])[1]) || 0;
  const recommendations = parseVulnerabilityRows(lines);
  const targets = parseHostTargets(lines);
  return {
    totalVulnerabilities: total || low + medium + high + critical || recommendations.length,
    lowCount: low,
    mediumCount: medium,
    highCount: high || recommendations.filter((row) => row.severity === 'High').length,
    criticalCount: critical || recommendations.filter((row) => row.severity === 'Critical').length,
    scannedHosts: hosts || targets.length,
    maxScore: maxScore || Math.max(...recommendations.map((row) => row.score), 0),
    monthYear: detectScanCompletedMonthYear(text) || detectMonthYear(text),
    reportDate: detectReportDate(text),
    targets,
    recommendations,
  };
}

export async function extractPdfText(file: File) {
  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await (pdfjsLib as any).getDocument({ data }).promise;
  const pages: string[] = [];
  let firstPageImageBytes: Uint8Array | undefined;
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    if (pageNumber === 1) {
      const viewport = page.getViewport({ scale: 1.35 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
        firstPageImageBytes = base64ToUint8Array(canvas.toDataURL('image/png').split(',')[1] || '');
      }
    }
    const content = await page.getTextContent();
    pages.push(content.items.map((item: any) => item.str).join('\n'));
  }
  return { text: pages.join('\n'), pageCount: pdf.numPages, firstPageImageBytes };
}

export async function createFilledDocxFromTemplate({
  project,
  task,
  pdfSource,
  validatedTargetsForReport,
}: {
  project: ProjectOption;
  task: TaskOption | null;
  pdfSource: ImportedPdfSource;
  validatedTargetsForReport?: TargetAssetRow[];
}) {
  const response = await fetch(TEMPLATE_URL);
  if (!response.ok) throw new Error('Gabarit DOCX introuvable');
  const templateBuffer = await response.arrayBuffer();
  const zip = new PizZip(templateBuffer);
  const documentFile = zip.file('word/document.xml');
  if (!documentFile) throw new Error('Structure DOCX invalide: word/document.xml absent');

  const summary = pdfSource.parsed;
  const generatedDate = new Date();
  const monthYear = summary.monthYear || generatedDate.toLocaleDateString('fr-CA', { month: 'long', year: 'numeric' });
  const formattedDate = summary.reportDate || generatedDate.toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  const clientName = project.nom_client || project.title || 'Client à préciser';
  const targetsForReport = validatedTargetsForReport?.length ? validatedTargetsForReport : summary.targets;
  const probeNarrative = buildProbeNarrative(targetsForReport);
  const replacements = {
    'Québec, le [Date]': `Québec, ${formattedDate}`,
    '[Date]': formattedDate,
    'Cher / Chère [Nom du destinataire],': `Chers membres de l’équipe de ${clientName},`,
    '[Nom du destinataire]': `membres de l’équipe de ${clientName}`,
    '[Mois Année]': monthYear,
    '[mois année]': monthYear,
    '[Nom du client]': clientName,
    'Le(s)': probeNarrative.deviceLead.startsWith('Les') ? 'Les' : 'Le',
    'dispositif(s)': probeNarrative.deviceLead.startsWith('Les') ? 'dispositifs' : 'dispositif',
    'ont été configurés': probeNarrative.deviceLead.startsWith('Les') ? 'ont été configurés' : 'a été configuré',
    'les outils de Carson & Saint qui a permis': 'les outils de Carson & Saint qui ont permis',
    'Adresses accessibles de l’externe (adresse IP publique de [Nom du client] et site Web);': '',
    'Selon le type de cible, le balayage a été réalisé à l’aide de deux sources distinctes:': probeNarrative.intro,
    'Une sonde interne installée sur le réseau de [Nom du client];': probeNarrative.firstProbe,
    'Une sonde externe située chez BZ.': probeNarrative.otherProbes,
  };

  updateScanReportDocumentProperties(zip, clientName, monthYear);
  replaceTemplateTextInDocx(zip, replacements);
  let xml = zip.file('word/document.xml')?.asText() || documentFile.asText();
  xml = replaceInlineTextAcrossRuns(xml, {
    '[Nom du client]': clientName,
    '[Mois Année]': monthYear,
    '[mois année]': monthYear,
    'Mois Année': monthYear,
    'Selon le type de cible, le balayage a été réalisé à l’aide de deux sources distinctes:': probeNarrative.intro,
    'Une sonde interne installée sur le réseau de [Nom du client];': probeNarrative.firstProbe,
    'Une sonde externe située chez BZ.': probeNarrative.otherProbes,
    'Annexe A': 'annexe A',
  });
  xml = replaceParagraphContaining(xml, 'Mois Année', monthYear);
  xml = replaceParagraphContaining(xml, 'Selon le type de cible', probeNarrative.intro);
  xml = replaceParagraphContaining(xml, 'Une sonde interne installée sur le réseau', probeNarrative.firstProbe);
  xml = replaceParagraphContaining(xml, 'Une sonde externe située chez BZ', probeNarrative.otherProbes);
  xml = replaceParagraphContaining(xml, 'Cher / Chère', `Chers membres de l’équipe de ${clientName},`);
  xml = replaceParagraphContaining(xml, 'ont été configuré', `${probeNarrative.setupParagraph} ${probeNarrative.deviceLead} à l’aide des outils de Carson & Saint qui ont permis d’effectuer des analyses et de tirer des conclusions dans un rapport détaillé incluant :`);
  xml = replaceAnalysisParagraphs(xml, summary, pdfSource.pageCount);
  xml = fillTargetTableRows(xml, targetsForReport);
  xml = fillRecommendationTableRows(xml, summary.recommendations);
  replaceScanReportPreviewImage(zip, pdfSource.firstPageImageBytes);
  zip.file('word/document.xml', xml);
  finalizeGeneratedDocxXml(zip);
  return zip.generate({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
}

